import { useMemo } from 'react';
import { AnnotationPostType, useAnnotationMutations, usePostAnnotations } from '@/hooks/use-annotations';
import { useAnnotationPreferences } from '@/hooks/use-annotation-preferences';
import AnnotationPin from './AnnotationPin';
import { cn } from '@/lib/utils';

interface AnnotationPinsOverlayProps {
  postId: string;
  postType: AnnotationPostType;
  className?: string;
}

export function AnnotationPinsOverlay({ postId, postType, className }: AnnotationPinsOverlayProps) {
  const {
    preferences: { showAnnotations, includeSeen },
  } = useAnnotationPreferences();

  const { annotations, isLoading } = usePostAnnotations(postId, postType, {
    includeSeen: true, // Always fetch all annotations
    enabled: showAnnotations,
  });

  const { markSeen } = useAnnotationMutations(postId, postType);

  const filteredAnnotations = useMemo(() => {
    if (!annotations?.length) return [];
    // Always show all annotations - don't filter by seen status
    // The pin component will handle the visual fade for seen annotations
    return annotations;
  }, [annotations]);

  if (!showAnnotations || isLoading || filteredAnnotations.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-[100]',
        className,
      )}
      style={{ pointerEvents: 'none' }}
    >
      {filteredAnnotations.map((annotation) => (
        <AnnotationPin
          key={annotation.id}
          annotation={annotation}
          onMarkSeen={(id) => markSeen.mutateAsync(id)}
          pointerEvents="auto"
        />
      ))}
    </div>
  );
}

export default AnnotationPinsOverlay;
